import store from "store/index";

import { reportApiError, reportGraphqlErrors, endpointOf, operationFromBody } from "./reportApiError";
import { startApiSpan, endApiSpan } from "./apiTiming";

export const getGraphQLHeaders = () => {
  const myHeaders = new Headers();
  myHeaders.append("Authorization", store?.getState()?.property?.api_key || "myhyly-hyblast-sync");
  myHeaders.append("Content-Type", "application/json");

  return myHeaders;
};

// silent-mode fallback: callers must not rely on the body shape — `.json()`
// resolves to `{}` so chained destructures fail soft instead of throwing.
const noopResponse = () => ({
  ok: false,
  status: 0,
  json: () => Promise.resolve({}),
});

const wrapNetworkError = (err) => ({
  isNetworkError: true,
  message: err?.message || "Network unavailable",
  originalError: err,
});

const wrapHttpError = (response) => ({
  isHttpError: true,
  status: response.status,
  message: `HTTP ${response.status}`,
});

export const graphqlFetch = async (url, options, extra = {}) => {
  const silent = Boolean(extra && extra.silent);

  // Time this request as a standalone http.client span so it shows up in Sentry even though
  // no pageload transaction is active here (see apiTiming.js). Concurrency-safe: the span
  // lives in this call's closure, so overlapping graphqlFetch calls never collide.
  const span = startApiSpan({ client: "graphql", method: "POST", endpoint: endpointOf(url), operation: operationFromBody(options?.body) });

  let status;
  let errored = false;

  try {
    let response;

    try {
      response = await fetch(url, options);
    } catch (err) {
      errored = true;
      const wrapped = wrapNetworkError(err);

      if (silent) {
        console.warn("[graphqlFetch] network error (silent):", wrapped.message);

        return noopResponse();
      }

      console.warn("[graphqlFetch] network error:", wrapped.message);

      // Network failure has no HTTP status -> invisible to httpClientIntegration. Capture it
      // (non-silent only; silent telemetry/feedback mutations stay silent by design).
      reportApiError(err, {
        client: "graphql",
        kind: "network",
        endpoint: endpointOf(url),
        operation: operationFromBody(options?.body),
      });

      return Promise.reject(wrapped);
    }

    status = response.status;

    if (!response.ok) {
      errored = true;
      // HTTP-status failures are already captured by httpClientIntegration -> do not re-capture.
      const httpError = wrapHttpError(response);

      if (silent) {
        console.warn("[graphqlFetch] http error (silent):", httpError.message);

        return noopResponse();
      }

      console.warn("[graphqlFetch] http error:", httpError.message);

      return Promise.reject(httpError);
    }

    // A GraphQL 200-OK can still carry an `errors[]` payload -> invisible to status-code
    // capture. Inspect a clone so the caller's own `.json()` still reads the untouched body.
    if (!silent) {
      try {
        const body = await response.clone().json();

        if (Array.isArray(body?.errors) && body.errors.length) {
          errored = true;

          reportGraphqlErrors(body.errors, {
            endpoint: endpointOf(url),
            operation: operationFromBody(options?.body),
          });
        }
      } catch (_) {
        // non-JSON body — nothing to inspect
      }
    }

    return response;
  } finally {
    endApiSpan(span, { status, errored });
  }
};
